import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { classesAPI, schedulesAPI, termsAPI, siteMediaAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { Search, Filter, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import ClassCard from '../components/ClassCard';
import { CATEGORIES } from '../lib/utils';
import { FreeTrialBanner, FreeTrialCard } from '../components/FreeTrialCTA';
import SEO from '../components/common/SEO';

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [heroImage, setHeroImage] = useState(null); // Start with null to prevent flash
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const navigate = useNavigate();
  
  // Default hero image URL
  const DEFAULT_HERO = 'https://customer-assets.emergentagent.com/job_804fc0fc-bc16-4170-abf7-caf86e0f2ddf/artifacts/h8c66pne_Tap-015.JPG';

  useEffect(() => {
    fetchData();
    fetchHeroImage();
  }, []);

  const fetchHeroImage = async () => {
    try {
      const res = await siteMediaAPI.getPageSettings('classes');
      // Check both 'hero' and 'hero_image' sections for backwards compatibility
      const settings = Array.isArray(res.data) ? res.data : [];
      const heroSetting = settings.find(s => s.section === 'hero') || 
                          settings.find(s => s.section === 'hero_image');
      if (heroSetting?.url) {
        // Handle both absolute and relative URLs
        let imageUrl = heroSetting.url;
        if (imageUrl.startsWith('/api/')) {
          imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
        }
        setHeroImage(imageUrl);
      } else {
        // No custom hero, use default
        setHeroImage(DEFAULT_HERO);
      }
    } catch (error) {
      console.error('Failed to fetch hero image:', error);
      // Use default image on error
      setHeroImage(DEFAULT_HERO);
    }
  };

  const fetchData = async () => {
    try {
      const [classesRes, schedulesRes, termRes] = await Promise.all([
        classesAPI.getAll(),
        schedulesAPI.getAll(),
        termsAPI.getActive(),
      ]);
      setClasses(classesRes.data);
      setSchedules(schedulesRes.data);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = classes.filter(cls => {
    const matchesSearch = cls.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          cls.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || cls.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getSchedulesForClass = (classId) => {
    return schedules.filter(s => s.class_id === classId);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO page="classes" />
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Hero */}
      <section className="relative py-20 overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7000FF]/20 via-transparent to-[#FF007F]/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="font-accent text-base tracking-[0.3em] text-[#FF007F] uppercase">
                2026 Classes Now Open
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4 mb-4">
                DISCOVER YOUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] to-[#7000FF]">PASSION</span>
              </h1>
              <p className="text-lg text-white/60 max-w-2xl">
                From ballet to hip-hop, drama to singing - find the perfect class for every aspiring performer.
              </p>
            </div>
            <div className="relative">
              {/* Skeleton placeholder while loading */}
              {!heroImageLoaded && (
                <div className="w-full h-80 bg-gradient-to-br from-[#7000FF]/20 to-[#FF007F]/20 rounded-xl animate-pulse" />
              )}
              {heroImage && (
                <img 
                  src={heroImage} 
                  alt="UPA Dance Class"
                  className={`w-full h-80 object-cover rounded-xl shadow-2xl transition-opacity duration-300 ${heroImageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                  onLoad={() => setHeroImageLoaded(true)}
                />
              )}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-40 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/10 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                placeholder="Search classes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 bg-[#111] border-[#333] text-white placeholder:text-white/40"
                data-testid="search-classes"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48 h-11 bg-[#111] border-[#333] text-white" data-testid="filter-category">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Category Pills */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
            <Badge 
              variant={selectedCategory === 'all' ? 'default' : 'secondary'}
              className={`cursor-pointer whitespace-nowrap ${selectedCategory === 'all' ? 'bg-[#FF007F] hover:bg-[#D6006A]' : 'bg-[#222] hover:bg-[#333] text-white/70'}`}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </Badge>
            {CATEGORIES.slice(0, 6).map(cat => (
              <Badge 
                key={cat.value}
                variant={selectedCategory === cat.value ? 'default' : 'secondary'}
                className={`cursor-pointer whitespace-nowrap ${selectedCategory === cat.value ? 'bg-[#FF007F] hover:bg-[#D6006A]' : 'bg-[#222] hover:bg-[#333] text-white/70'}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Free Trial CTA Banner */}
      <FreeTrialBanner variant="vibrant" />

      {/* Classes Grid */}
      <section className="py-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-[#111] rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : filteredClasses.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-[#111] rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-white/40" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">No classes found</h3>
              <p className="text-white/60 mb-4">Try adjusting your search or filters</p>
              <Button variant="outline" className="border-[#333] text-white hover:bg-[#222]" onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-white/50 mb-6">{filteredClasses.length} classes found</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {filteredClasses.map(cls => (
                  <ClassCard 
                    key={cls.id} 
                    classData={cls} 
                    schedules={getSchedulesForClass(cls.id)}
                    showAvailability={true}
                  />
                ))}
                {/* Free Trial Card in grid */}
                <FreeTrialCard />
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Classes;
